import camelcaseKeys from 'camelcase-keys'

import Activity from '@src/data/activity'
import WorkoutData from '@src/data/workout-data'
import TrainingPlan from '@src/data/training-plan'

export type RecentActivity = [{ date: string, performedExerciseCount: number }]

export type TrainingPlanProgress = {
  completed: number
  started: number
  skipped: number
  planned: number
  total: number
}

type ActivityLevel = 'low' | 'medium' | 'high'
export type FeedbackVerbosity = 'none' | 'dots_only' | 'full'

export enum ExpertiseLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

export enum MemberState {
  DEACTIVATED = 'deactivated',
  PUBLISHED = 'published',
  INTERN = 'intern'
}

export class Progression {
  attendanceAlert: boolean = false // last visit was more than 2 weeks ago
  averageIntensityRating: number | null = null // rpe value
  currentWorkoutAlert: boolean = false // 40% of the active workout exercises need to be changed
  easyWorkoutAlert: boolean = false// average intensity rating for the last three workouts is less than 6/10 -> show averageIntensityRating
  exercisesToChangeDown: number = 0
  exercisesToChangeUp: number = 0
  lastWorkoutDate: string | null = null
  staleWorkoutAlert: boolean = false// the same workout has been used for more than 2 weeks
  staleCheckupAlert: boolean = false// no checkup in the past 50 days
  plannedCheckupAlert: boolean = false

  attendanceAlert14: boolean = false// last visit was more than 14 days ago
  attendanceAlert30: boolean = false // last visit was more than 30 days ago
  attendanceAlert60: boolean = false // last visit was more than 60 days ago

  constructor (attributes) {
    Object.assign(this, attributes)

    this.buildAttendanceAlert(attributes)
  }

  buildAttendanceAlert (attributes) {
    this.attendanceAlert14 = attributes.attendance14DaysAlert
    this.attendanceAlert30 = attributes.attendance30DaysAlert
    this.attendanceAlert60 = attributes.attendance60DaysAlert
    this.attendanceAlert = this.attendanceAlert14 || this.attendanceAlert30 || this.attendanceAlert60
  }
}

export class MemberPermissions {
  changeIndividualStationWorkout: boolean
  changeActiveStationWorkout: boolean
  createStationWorkout: boolean

  changeIndividualTrainingPlan: boolean
  changeActiveTrainingPlan: boolean
  createTrainingPlan: boolean

  changeActiveHomeWorkout: boolean
  createHomeWorkout: boolean

  homeWorkout: boolean

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

export class CurrentActivityGoal {
  value: number
  completed: number

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

export default class Member {

  public id: number
  public username: string
  public firstName: string
  public lastName: string
  public gender: string
  public clubRoleType: string
  public displayName: string
  public state: MemberState
  public email: string | null
  public birthdate: string
  public code: string
  public codeSerial: string
  public codeNumber: string
  public locale: string // web platform language
  public displayLocale: string // station language
  public mobile: boolean // member is using the mobile apps

  public addressLine1: string
  public addressLine2: string
  public addressPostcode: string
  public addressTownCity: string
  public addressCountry: string
  public contactNumber: string

  public emergencyContactName: string
  public emergencyContactRelationship: string
  public emergencyContactNumber: string
  public avatarOriginalUrl: string
  public avatar: object

  public termsReacceptanceRequired: boolean
  public progression: Progression
  public favoriteActivities: Activity[]

  public activeWorkout: WorkoutData
  public homeWorkout: WorkoutData
  public currentTrainingPlan: TrainingPlan

  public activityLevel: ActivityLevel
  public expertiseLevel: ExpertiseLevel
  public feedbackVerbosity: FeedbackVerbosity
  public displayPixScore: boolean
  public emailNotifications: string[]

  public recentActivity: RecentActivity
  public trainingPlanProgress: TrainingPlanProgress

  // array of the top 2 start hours in 24 hr format
  public usualStartTimes: number[]
  public hiddenFromLeaderboard: boolean

  public initialCheckupId: number
  // date when the user registers; null represents an unregistered user
  public registeredAt: string
  public scheduledDeactivationDate: string | null

  public memberPermissions: MemberPermissions

  public currentActivityGoal: CurrentActivityGoal

  public detailsLoaded = false

  constructor (attributes?) {
    this.emailNotifications = []
    Object.assign(this, attributes)
    if (attributes) {
      if (attributes.progression) {
        this.progression = new Progression(camelcaseKeys(attributes.progression, { deep: true }))
      }
      if (attributes.activeWorkout) {
        this.activeWorkout = new WorkoutData(camelcaseKeys(attributes.activeWorkout, { deep: true }))
      }
      if (attributes.homeWorkout) {
        this.homeWorkout = new WorkoutData(camelcaseKeys(attributes.homeWorkout, { deep: true }))
      }
      if (attributes.currentTrainingPlan) {
        this.currentTrainingPlan = new TrainingPlan(camelcaseKeys(attributes.currentTrainingPlan, { deep: true }))
      }
      if (attributes.memberPermissions) {
        this.memberPermissions = new MemberPermissions(camelcaseKeys(attributes.memberPermissions, { deep: true }))
      }
      if (attributes.usualStartTimes) {
        this.usualStartTimes = attributes.usualStartTimes.sort((a, b) => a - b)
      }
    }
  }

  get fullName () {
    return `${this.firstName || ''} ${this.lastName || ''}`
  }

  get trainingProgress () {
    const trainingDays = this.trainingPlanProgress
    if (!trainingDays) return 0
    return trainingDays.completed / trainingDays.total
  }

  get lastActivity () {
    return (this.recentActivity || []).slice().sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0] || {}
  }

  get lastActivityDate () {
    return this.lastActivity.date
  }

  get age () {
    if (!this.birthdate) {
      return '-'
    }
    let milliseconds = Date.now() - new Date(this.birthdate).getTime()
    let age = new Date(milliseconds) // miliseconds from epoch
    return Math.abs(age.getUTCFullYear() - 1970)
  }

}
